import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/feature/MovieCard';
import { movieCategories, getMoviesByCategory, allMovies } from '@/mocks/movies';

export default function Home() {
  const [activeCategory, setActiveCategory] = useState('all');

  const displayedMovies = useMemo(() => {
    if (activeCategory === 'all') {
      return allMovies;
    }
    return getMoviesByCategory(activeCategory);
  }, [activeCategory]);

  const featuredMovies = useMemo(() => allMovies.filter(m => m.isHot), []);
  const latestMovies = useMemo(() => allMovies.filter(m => m.isNew), []);
  const hotCategoryMovies = useMemo(() => allMovies.filter(m => m.rating >= 9), []);

  const stats = [
    { value: '50,000+', label: '收录电影', icon: 'ri-movie-line' },
    { value: '1,200万+', label: '活跃用户', icon: 'ri-user-line' },
    { value: '99.9%', label: '播放成功率', icon: 'ri-check-double-line' },
    { value: '24h', label: '片源更新', icon: 'ri-time-line' },
  ];

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '被窝影院',
    url: 'https://beiwocinema.com',
    description: '被窝影院电影大全免费看，最新电影免费看和高分电影在线，电影大全无广告，院线电影免费经典电影全集，不缓冲流畅在线观看。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://beiwocinema.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '被窝影院',
    url: 'https://beiwocinema.com',
    description: '专业电影在线观看平台',
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Hero Section */}
        <header className="relative w-full h-[500px] md:h-[650px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Dark%20cinematic%20theater%20interior%20with%20warm%20golden%20spotlight%20projector%20beam%20cutting%20through%20darkness%20elegant%20velvet%20seats%20empty%20auditorium%20atmospheric%20dramatic%20lighting%20film%20reel%20aesthetic%20luxurious%20cinema%20hall&width=1920&height=1080&seq=hero-cinema-bg&orientation=landscape"
            alt="被窝影院 - 在线电影平台"
            className="absolute inset-0 w-full h-full object-cover object-center"
            title="被窝影院"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-black/70"></div>
          <div className="absolute inset-0 flex items-center justify-center px-4 md:px-8">
            <div className="w-full max-w-4xl text-center">
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-4 leading-tight">
                被窝里的<span className="text-accent-400">电影世界</span>
              </h1>
              <p className="text-base md:text-lg text-white/80 mb-8 max-w-2xl mx-auto leading-relaxed">
                最新电影免费看，高分电影在线。无广告不缓冲，被窝影院电影大全满足你的所有观影需求。院线大片、经典佳作、热门新片一网打尽。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/category/all"
                  className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-full font-medium text-sm transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-play-circle-line mr-2"></i>立即观影
                </Link>
                <Link
                  to="/category/classic"
                  className="px-8 py-3 border border-white/40 hover:border-white/80 text-white rounded-full font-medium text-sm transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-vip-crown-line mr-2"></i>经典必看
                </Link>
              </div>
            </div>
          </div>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 animate-bounce">
            <i className="ri-arrow-down-s-line text-white/60 text-2xl"></i>
          </div>
        </header>

        {/* Stats Section */}
        <section className="w-full px-4 md:px-8 -mt-10 relative z-10">
          <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.map((stat) => (
              <div key={stat.label} className="bg-background-50 rounded-lg p-5 text-center border border-background-200/60">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center bg-accent-100 rounded-full">
                  <i className={`${stat.icon} text-accent-500 text-lg`}></i>
                </div>
                <p className="text-xl md:text-2xl font-bold text-foreground-900">{stat.value}</p>
                <p className="text-xs text-foreground-500 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </section>

        <main className="w-full">
          {/* Category Tabs */}
          <section className="w-full px-4 md:px-8 mt-16 md:mt-20">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900">
                  探索电影
                </h2>
                <Link to="/category/all" className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer">
                  查看全部 <i className="ri-arrow-right-line ml-1"></i>
                </Link>
              </div>
              <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide flex-wrap">
                {movieCategories.map((cat) => (
                  <button
                    key={cat.slug}
                    onClick={() => setActiveCategory(cat.slug)}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer flex items-center gap-1.5 ${
                      activeCategory === cat.slug
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200'
                    }`}
                  >
                    <i className={`${cat.icon} text-sm`}></i>
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* Movie Grid - By Category */}
          <section className="w-full px-4 md:px-8 mt-8 md:mt-10">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
                {displayedMovies.slice(0, 12).map((movie) => (
                  <MovieCard key={movie.id} {...movie} />
                ))}
              </div>
              {displayedMovies.length === 0 && (
                <div className="text-center py-16 text-foreground-500">
                  <i className="ri-movie-line text-4xl mb-3 block"></i>
                  <p>该分类暂无电影，请查看其他分类</p>
                </div>
              )}
            </div>
          </section>

          {/* Featured Movies */}
          {featuredMovies.length > 0 && (
            <section className="w-full px-4 md:px-8 mt-16 md:mt-20">
              <div className="max-w-7xl mx-auto">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900">
                      <i className="ri-fire-line text-accent-500 mr-2"></i>热门推荐
                    </h2>
                    <p className="text-sm text-foreground-500 mt-1">万人好评的高分佳作</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {featuredMovies.slice(0, 3).map((movie) => (
                    <Link
                      key={movie.id}
                      to={`/article/${movie.id}`}
                      className="group flex gap-4 p-4 rounded-lg bg-background-50 border border-background-200/60 hover:border-primary-300 transition-all cursor-pointer"
                    >
                      <div className="w-28 h-36 flex-shrink-0 rounded-lg overflow-hidden">
                        <img
                          src={movie.image}
                          alt={movie.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          title={`${movie.title} - 被窝影院高清在线观看`}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-base font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors truncate">
                            {movie.title}
                          </h3>
                          <span className="flex items-center gap-0.5 text-accent-500 text-xs whitespace-nowrap">
                            <i className="ri-star-fill text-xs"></i>{movie.rating}
                          </span>
                        </div>
                        <p className="text-xs text-foreground-500 mb-2">{movie.titleEn} · {movie.year} · {movie.duration}</p>
                        <p className="text-xs text-foreground-600 line-clamp-2 leading-relaxed mb-2">{movie.description}</p>
                        <div className="flex gap-1 flex-wrap">
                          {movie.tags.map(tag => (
                            <span key={tag} className="px-1.5 py-0.5 bg-secondary-100 text-secondary-700 text-xs rounded whitespace-nowrap">{tag}</span>
                          ))}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Latest Movies */}
          {latestMovies.length > 0 && (
            <section className="w-full px-4 md:px-8 mt-16 md:mt-20">
              <div className="max-w-7xl mx-auto">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900">
                      <i className="ri-flashlight-line text-primary-500 mr-2"></i>最新上架
                    </h2>
                    <p className="text-sm text-foreground-500 mt-1">刚刚更新的热门新片</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
                  {latestMovies.map((movie) => (
                    <MovieCard key={movie.id} {...movie} />
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* High Rated Section */}
          <section className="w-full px-4 md:px-8 mt-16 md:mt-20">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900">
                    <i className="ri-star-fill text-accent-500 mr-2"></i>高分佳作
                  </h2>
                  <p className="text-sm text-foreground-500 mt-1">评分 9.0+ 的口碑神作</p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
                {hotCategoryMovies.map((movie) => (
                  <MovieCard key={movie.id} {...movie} />
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="w-full px-4 md:px-8 mt-16 md:mt-20 mb-16 md:mb-20">
            <div className="max-w-7xl mx-auto">
              <div className="relative rounded-2xl overflow-hidden bg-background-900 py-16 md:py-20 px-6 md:px-12 text-center">
                <img
                  src="https://readdy.ai/api/search-image?query=Abstract%20dark%20cinematic%20background%20with%20soft%20golden%20light%20particles%20floating%20film%20reel%20silhouette%20elegant%20atmospheric%20texture%20luxurious%20theater%20ambiance&width=1400&height=600&seq=cta-cinema-dark&orientation=landscape"
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover opacity-30"
                />
                <div className="relative z-10">
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-3">
                    今晚，在被窝里看一部好电影
                  </h2>
                  <p className="text-white/70 mb-8 max-w-xl mx-auto text-sm md:text-base">
                    无论你喜欢动作、喜剧、科幻还是经典老片，被窝影院都有你的菜。无广告，不缓冲，随时随地流畅观影。
                  </p>
                  <Link
                    to="/category/all"
                    className="inline-flex items-center px-8 py-3 bg-accent-500 hover:bg-accent-600 text-white rounded-full font-medium text-sm transition-all whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-play-circle-line mr-2 text-lg"></i>开始探索
                  </Link>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}